import React, { createRef, PureComponent, ReactNode, RefObject } from 'react'
import { Cluster, Marker, MarkerClusterer } from '@googlemaps/markerclusterer'
import moment from 'moment'
import { calculateMinutesToArrive, flatten, getRegion, loadGoogleMaps } from '../../helpers'
import { ICoordinate, IExpeditionType, ILocation, ISearchedType, IServiceInfo, IStop, IStopAvailabilityTimes } from '../../models'
import theme from '../../theme'
import StyledDiv from './styles'
import { LiteralsService } from '../../services'
import { MapPin, StopMarker } from '../../components'
import { images } from '../../images'
import { fetchStopAvailabilityTimesAction, resetStopAvailabilityTimesAction, searchStopCoordinatesAction } from '../../store/actions'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../helpers/connectRedux'
import { IStoreState } from '../../store/states'
import { AnyAction } from 'redux'


interface IProps extends DispatchProp<AnyAction> {
  path?: any
  serviceInfos: IServiceInfo[]
  inStop?: IStop | null
  outStop?: IStop | null
  outBusStops?: IStop[]
  busLocation?: ICoordinate | null
  correction?: number
  zoomLevel?: number
  isClusterMap?: boolean
  controlsEnabled?: boolean
  onMarkerClick?: (stop: IStop) => void
  areaChanged?: boolean
  onInfoWindowShow?: (opened: boolean) => void
  centerChanged?: ICoordinate | null
  showRecenterButton?: boolean
  showFullscreenControl?: boolean
  userLocation?: ICoordinate
  onSetUserLocation?: () => void
  restrictedOrigins?: number[]
  // door to door: pin the map centre and report where the user drops it
  showCenterPin?: boolean
  centerPinIsDestination?: boolean
  centerPinOutsideArea?: boolean
  onCenterSettled?: (coords: ICoordinate, nearbyStop?: IStop) => void
  // theoretical / live times of the stop shown in the info window
  onShowSchedules?: (stop: IStop) => void
  stopAvailabilityTimes: IStopAvailabilityTimes[]
}

const CAMERA_GLIDE_MS = 650
const STOP_SNAP_PIXELS = 50
const DEPARTURES_SHOWN_IN_INFO_WINDOW = 3

interface IState {
  isMapCentered: boolean
  forwardDirection: boolean
  isMapMoving: boolean
}

class MapContainer extends PureComponent<IProps, IState> {
  public map!: google.maps.Map
  private mapRef: RefObject<HTMLDivElement | null>
  private infoWindow!: google.maps.InfoWindow
  private markers!: google.maps.marker.AdvancedMarkerElement[]
  private clusterer!: MarkerClusterer
  private polygons: google.maps.Polygon[] = []
  private userLocationMarker!: google.maps.marker.AdvancedMarkerElement
  private busLocationMarker!: google.maps.marker.AdvancedMarkerElement
  private lastReportedCenter?: google.maps.LatLngLiteral
  private cameraAnimation?: number
  private userDragged = false
  private isProgrammaticCameraMove = false
  // drawMap awaits the Maps API, so without this every update in the meantime
  // would start building a second map and orphan the markers of the first.
  private isCreatingMap = false
  // the stop whose info window is open, so its departures can be redrawn when they arrive
  private openedStopInfo?: { stop: IStop, serviceInfo: IServiceInfo }

  private get noOutline(): boolean {
    const { serviceInfos } = this.props

    return serviceInfos.every(s => !s.outlines)
  }

  private get region(): any {
    const { serviceInfos, isClusterMap, inStop, outStop } = this.props
    const serviceInfosNotSelected = this.props.serviceInfos.every((serviceInfo: IServiceInfo) => !serviceInfo.selected)

    if (serviceInfos && serviceInfos.length && isClusterMap) {
      return getRegion({
        features: serviceInfos
            .filter(s => serviceInfosNotSelected ? s : s.selected)
            .map(s => s.stops.map(st => st.location))
            .reduce(flatten, [])
      })
    } else if (!isClusterMap && (inStop || outStop)) {
      let completeFeatures: any = []
      if (inStop) completeFeatures = [completeFeatures, inStop.location].concat()
      if (outStop) completeFeatures = [completeFeatures, outStop.location].concat()
      return getRegion({
        features: completeFeatures
      })
    }

    return null
  }

  constructor(props: IProps) {
    super(props)
    this.mapRef = createRef<HTMLDivElement>()

    this.state = {
      isMapCentered: true,
      forwardDirection: true,
      isMapMoving: false
    }
  }

  public componentDidMount(): void {
    this.drawMap()
    this.drawUserLocationMarker()
  }

  public componentWillUnmount(): void {
    if (this.cameraAnimation) cancelAnimationFrame(this.cameraAnimation)
  }

  public componentDidUpdate(prevProps: IProps): void {
    const { areaChanged, centerChanged, serviceInfos, userLocation, isClusterMap, inStop, outStop, busLocation, outBusStops, controlsEnabled, zoomLevel } = this.props
    const { isMapCentered } = this.state

    if (!this.map) {
      this.drawMap()
      return
    }

    if (prevProps.controlsEnabled !== controlsEnabled) {
      this.map.setOptions({
        zoomControl: !!controlsEnabled,
        fullscreenControl: !!controlsEnabled && this.props.showFullscreenControl !== false,
        draggable: !!controlsEnabled
      })
    }

    if (prevProps.isClusterMap !== isClusterMap) {
      this.tearDownMarkers()
      this.clearOutlines()
      this.drawServiceOutlines()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
      if (zoomLevel) this.map.setZoom(zoomLevel)
      return
    }

    const markersNeedUpdate = isClusterMap && (
      prevProps.inStop !== inStop ||
      prevProps.outStop !== outStop ||
      prevProps.serviceInfos !== serviceInfos ||
      prevProps.outBusStops !== outBusStops
    )

    if (markersNeedUpdate) {
      this.updateMarkersIcon()
      this.setMapBoundariesAndZoom()
    }

    if (!isClusterMap && (prevProps.inStop !== inStop || prevProps.outStop !== outStop)) {
      this.tearDownMarkers()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
    }

    if (!isMapCentered && ((prevProps.inStop && !inStop) || (prevProps.outStop && !outStop))) this.handleRecenterClick()

    if (centerChanged) this.updateMapLocation(centerChanged.latitude, centerChanged.longitude)
    if (userLocation) this.drawUserLocationMarker()
    if (areaChanged) this.updateMapLocation(this.region.latitude, this.region.longitude)
    if (prevProps.serviceInfos !== serviceInfos) {
      this.clearOutlines()
      this.drawServiceOutlines()
      this.clusterer && this.clusterer.clearMarkers()
      this.drawMarkers()
      this.setMapBoundariesAndZoom()
    }
    if (busLocation) this.drawBusLocation()
    else if (this.busLocationMarker) this.busLocationMarker.map = null

    if (prevProps.stopAvailabilityTimes !== this.props.stopAvailabilityTimes) this.refreshMarkerInfo()
  }

  private tearDownMarkers(): void {
    if (this.clusterer) {
      this.clusterer.clearMarkers()
    }
    if (this.markers) {
      this.markers.forEach(m => { m.map = null })
      this.markers = []
    }
  }

  public render(): ReactNode {
    const { showRecenterButton, showCenterPin } = this.props
    const { isMapCentered } = this.state

    return (
      <>
        <StyledDiv className='markers-options'>
          <StopMarker id='normalMarker' color={ theme.colors.black } />
          <StopMarker id='originMarker' color={ theme.colors.blueLight } text={ LiteralsService.get('origin', true) } textColor={ theme.colors.white } />
          <StopMarker id='destinationMarker' color={ theme.colors.greenRadio } text={ LiteralsService.get('destination', true) } />
          <StopMarker id='disabledMarker' color={ theme.colors.greyMedium } />
          <StopMarker id='originDot' color={ theme.colors.blueLight } />
          <StopMarker id='destinationDot' color={ theme.colors.greenRadio } />
        </StyledDiv>
        <StyledDiv id='persistent-map-mount'>
          <StyledDiv id="map" className="map" ref={ this.mapRef } />
          {
            showCenterPin &&
            <div className='center-pin'>
              <MapPin
                isDestination={ this.props.centerPinIsDestination }
                outsideArea={ this.props.centerPinOutsideArea }
                flying={ this.state.isMapMoving }
              />
            </div>
          }
          {
            showRecenterButton &&
            <div className='recenter-container' onClick={ () => this.handleRecenterClick() }>
              <img src={ isMapCentered ? images['myLocation'] : images[`area`] } alt='recenter' />
            </div>
          }
        </StyledDiv>
      </>
    )
  }

  private async drawMap(): Promise<void> {
    if (this.isCreatingMap || this.map) return
    this.isCreatingMap = true

    try {
    await loadGoogleMaps()
    const { controlsEnabled, zoomLevel} = this.props
    const latitude = this.region?.latitude
    const longitude = this.region?.longitude

    if (this.mapRef && this.mapRef.current && latitude && longitude) {
      this.map = new google.maps.Map(this.mapRef.current, {
        center: { lat: latitude, lng: longitude },
        controlSize: 26,
        zoom: zoomLevel ?? 14,
        zoomControl: !!controlsEnabled,
        zoomControlOptions: {
          position: 3
        },
        fullscreenControl: !!controlsEnabled && this.props.showFullscreenControl !== false,
        draggable: !!controlsEnabled,
        panControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        mapId: process.env.REACT_APP_GOOGLE_MAP_ID
      })
      ;(window as any).__persistentMap = this.map
      ;(window as any).__refreshPersistentMap = () => {
        if (!this.map) return
        google.maps.event.trigger(this.map, 'resize')
        if (this.clusterer && this.markers && this.markers.length) {
          this.clusterer.clearMarkers()
          this.clusterer.addMarkers(this.markers)
        } else if (this.markers && this.markers.length) {
          this.markers.forEach(m => { m.map = null })
          this.markers.forEach(m => { m.map = this.map })
        }
        if (this.markers && this.markers.length) this.setMapBoundariesAndZoom()
      }

      this.initInfoWindow()
      this.drawMarkers()
      this.drawRoute()
      this.drawServiceOutlines()
      this.setMapBoundariesAndZoom()

      this.map.addListener('dragstart', () => {
        this.userDragged = true
        this.setState({
          isMapCentered: false,
          isMapMoving: true
        })
      })

      this.map.addListener('zoom_changed', () => {
        if (this.isProgrammaticCameraMove) return
        this.setState({
          isMapCentered: false
        })
      })

      this.map.addListener('click', () => {
        if (this.infoWindow.isOpen) this.infoWindow.close()
      })

      this.map.addListener('idle', () => {
        this.setState({ isMapMoving: false })
        this.reportCenter()
      })
    }
    } finally {
      // Released so a later update can retry: the first attempt happens before
      // the services arrive, and without them there is no region to frame.
      this.isCreatingMap = false
    }
  }

  private reportCenter(): void {
    const { onCenterSettled } = this.props
    const center = this.map?.getCenter()
    if (!onCenterSettled || !center) return

    const latitude = center.lat()
    const longitude = center.lng()

    // 'idle' repeats on resizes and re-renders too, and each report costs a
    // reverse geocode, so only a centre that actually moved is worth one.
    const last = this.lastReportedCenter
    if (last && Math.abs(last.lat - latitude) < 1e-6 && Math.abs(last.lng - longitude) < 1e-6) return

    // Framing the map on entry or recentring should still show the address,
    // but only a deliberate drag may pick a stop on the user's behalf.
    const dragged = this.userDragged
    this.userDragged = false

    this.lastReportedCenter = { lat: latitude, lng: longitude }
    onCenterSettled({ latitude, longitude }, dragged ? this.stopNearCenter(center) : undefined)
  }

  // Dropping the pin on top of a real stop picks it, mirroring the app: the
  // reach is measured on screen, so it follows the zoom.
  private stopNearCenter(center: google.maps.LatLng): IStop | undefined {
    const { serviceInfos } = this.props
    const projection = this.map?.getProjection()
    if (!projection || !serviceInfos?.length) return undefined

    const centerPoint = projection.fromLatLngToPoint(center)
    const stops = serviceInfos.flatMap(serviceInfo => serviceInfo.stops)
    if (!centerPoint || !stops.length) return undefined

    const scale = Math.pow(2, this.map.getZoom() ?? 0)
    const closest = stops
      .map(stop => {
        const [lng, lat] = stop.location.geometry.coordinates
        const point = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng))
        const distance = point
          ? Math.hypot((point.x - centerPoint.x) * scale, (point.y - centerPoint.y) * scale)
          : Infinity

        return { stop, distance }
      })
      .reduce((nearest, candidate) => candidate.distance < nearest.distance ? candidate : nearest)

    if (closest.distance > STOP_SNAP_PIXELS) return undefined

    // Services share stops at identical coordinates, so take one that is
    // actually selectable — the same choice tapping the marker would make.
    const [closestLng, closestLat] = closest.stop.location.geometry.coordinates
    return stops.find(stop => {
      const [lng, lat] = stop.location.geometry.coordinates
      return lng === closestLng && lat === closestLat && !this.isDisabled(stop)
    })
  }

  private initInfoWindow() {
    const { onInfoWindowShow } = this.props

    const handleInfoWindowChange = (opened: boolean) => {
      if (onInfoWindowShow && typeof onInfoWindowShow === 'function') onInfoWindowShow(opened)
    }

    this.infoWindow = new google.maps.InfoWindow()
    google.maps.event.addListener(this.infoWindow, 'close', () => handleInfoWindowChange(false))
    google.maps.event.addListener(this.infoWindow, 'visible', () => handleInfoWindowChange(true))
    google.maps.event.addListener(this.map, 'drag', () => {
      if (this.infoWindow.isOpen) this.infoWindow.close()
    })
  }

  private drawMarkers(): any {
    const { isClusterMap, serviceInfos, inStop, outStop, correction } = this.props
    const latCorrection = correction ?? 0

    if (isClusterMap && serviceInfos) this.fillClusterMarkers()
    else if (!isClusterMap) {
      this.markers = []
      if (inStop) {
        const beginMarker = document.getElementById('originDot')?.cloneNode(true)
        const originMarker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          gmpDraggable: false,
          position: {
            lat: inStop.location.geometry.coordinates[1] - latCorrection,
            lng: inStop.location.geometry.coordinates[0]
          },
          content: beginMarker
        })
        this.markers.push(originMarker)
      }
      if (outStop) {
        const endMarker = document.getElementById('destinationDot')?.cloneNode(true)
        const destinationMarker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          gmpDraggable: false,
          position: {
            lat: outStop.location.geometry.coordinates[1] - latCorrection,
            lng: outStop.location.geometry.coordinates[0]
          },
          content: endMarker
        })
        this.markers.push(destinationMarker)
      }
    }
  }

  private async drawUserLocationMarker(): Promise<void> {
    await loadGoogleMaps()
    const { userLocation, onSetUserLocation } = this.props
    this.userLocationMarker && this.userLocationMarker.remove();
    if (userLocation) {
      const userInfoImg = document.createElement('img')
      userInfoImg.src = images.userInfo
      userInfoImg.width = 24
      userInfoImg.height = 24
  
      const pin = new google.maps.marker.PinElement({
        scale: 1.5,
        background: '#FFF',
        glyph: userInfoImg,
      })
  
      this.userLocationMarker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        gmpDraggable: false,
        position: {
          lat: userLocation.latitude,
          lng: userLocation.longitude
        },
        content: pin.element
      })
      
      const userLocationLatLng = new google.maps.LatLng(userLocation.latitude, userLocation.longitude);
      this.map.setCenter(userLocationLatLng);
      this.setState({
        isMapCentered: false
      })
      onSetUserLocation && onSetUserLocation()
    }
  }
  
  private async drawBusLocation() {
    const { busLocation } = this.props
    if (this.busLocationMarker) this.busLocationMarker.map = null
    if (busLocation) {
      const iconContainer = document.createElement('div')
      iconContainer.style.width = '30px'
      iconContainer.style.height = '30px'
      iconContainer.style.padding = '4px'
      iconContainer.style.borderRadius = '50%'
      iconContainer.style.border = `2px solid ${busLocation.color}`
      iconContainer.style.backgroundColor = 'white'
      iconContainer.style.display = 'flex'
      iconContainer.style.justifyContent = 'center'
      iconContainer.style.alignItems = 'center'

      const busIcon = document.createElement('div')
      busIcon.style.width = '24px'
      busIcon.style.height = '24px'
      busIcon.style.backgroundColor = busLocation.color || 'black'
      busIcon.style.mask = `url(${images.bus})`

      iconContainer.appendChild(busIcon)
  
      this.busLocationMarker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        gmpDraggable: false,
        position: {
          lat: busLocation.latitude,
          lng: busLocation.longitude
        },
        content: iconContainer
      })
    }
  }

  private getStopType(stop: IStop | undefined): string {
    if (stop) {
      const { inStop, outStop } = this.props
      const isOriginStop = stop.id === inStop?.id
      const isDestinationStop = stop.id === outStop?.id
      if ((!isOriginStop && !isDestinationStop) && this.isDisabled(stop)) return 'disabled'
      return isOriginStop
      ? 'origin'
      : isDestinationStop
        ? 'destination'
        : 'normal'
    }
    return 'normal'
  }

  private fillClusterMarkers(): void {
    const { serviceInfos, correction, inStop, outStop, onMarkerClick } = this.props
    const { forwardDirection } = this.state
    const latCorrection = correction ?? 0

    this.markers = [];

    const selectesServiceInfos = serviceInfos.filter((serviceInfo) => serviceInfo.selected)
    selectesServiceInfos.map(serviceInfo => {
      serviceInfo.stops.filter((s: any) => forwardDirection ? (s.direction == 0 || s.direction == null) : (s.direction == 1 || s.direction == null)).map(stop => {
        const lat = stop.location.geometry.coordinates[1] - latCorrection
        const lng = stop.location.geometry.coordinates[0]
        const isStopDisabled = this.isDisabled(stop)
        const stopType = this.getStopType(stop)
        let stopMarkerId = `${stopType}Marker`

        const markerIcon = document.getElementById(stopMarkerId)
        
        const marker = new google.maps.marker.AdvancedMarkerElement({
          gmpDraggable: false,
          position: { lat, lng },
          content: markerIcon?.cloneNode(true) as HTMLElement
          
        })
        marker.setAttribute('id', `${serviceInfo.id}_${stop.id}_${stopType}`)
        marker.addListener('click', () => {
          const stopData = { ...stop, serviceInfo }
          if (inStop && inStop.id === stop.id) {
            onMarkerClick && onMarkerClick(stopData)
          } else if (outStop && outStop.id === stop.id) {
            onMarkerClick && onMarkerClick(stopData)
          } else {
            if (!isStopDisabled) this.drawMarkerInfo(stop, marker, serviceInfo)
          }
        })
        if (!this.markers.find(marker => Number(marker.getAttribute('id')?.split('_')[1]) === stop.id)) this.markers.push(marker)
      })
    })

    if (this.clusterer) {
      this.clusterer.addMarkers(this.markers)
    } else {
      this.clusterer = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        renderer: {
          render: (cluster: Cluster) => this.renderCluster(cluster, serviceInfos)
        }
      })
    }
  }

  private clusterHasEveryStopType(clusterPoints: any, type: string) {
    return clusterPoints.every((point: any) => {
      const stopType = point.id.split('_')[2]
      return stopType === type
    })
  }

  private clusterHasSomeStopType(clusterPoints: any, type: string) {
    return clusterPoints.some((point: any) => {
      const stopType = point.id.split('_')[2]
      return stopType === type
    })
  }

  private renderCluster(cluster: Cluster, serviceInfos: IServiceInfo[]): Marker {
    const clusteredPoints = cluster.markers || []
    let clusterStopType = 'normal'

    if (this.clusterHasEveryStopType(clusteredPoints, 'disabled')) clusterStopType = 'disabled'
    else if (this.clusterHasSomeStopType(clusteredPoints, 'origin')) clusterStopType = 'origin'
    else if (this.clusterHasSomeStopType(clusteredPoints, 'destination')) clusterStopType = 'destination'

    const clusterIcon = document.getElementById(`${clusterStopType}Marker`)?.cloneNode(true) as HTMLElement
    if (clusterIcon) clusterIcon.style.scale = '1.2'
    return new google.maps.marker.AdvancedMarkerElement({
      gmpDraggable: false,
      position: cluster.position,
      content: clusterIcon
    })
  }

  private drawMarkerInfo(stop: IStop, marker: google.maps.marker.AdvancedMarkerElement, serviceInfo: IServiceInfo) {
    const selectedMarkerId = this.infoWindow.get('markerId')
    if (selectedMarkerId && selectedMarkerId === marker.id && this.infoWindow.isOpen) {
      this.infoWindow.close()
      this.openedStopInfo = undefined
      return
    }

    this.openedStopInfo = { stop, serviceInfo }
    this.infoWindow.close()
    this.infoWindow.setHeaderDisabled(true)
    this.infoWindow.setContent(this.buildMarkerInfoContent(stop, serviceInfo))
    this.infoWindow.set('markerId', marker.id)
    this.infoWindow.open(this.map, marker)

    // The timetable arrives from the BE, so the window is refreshed once it lands. Only
    // services publishing live times are worth asking about, as the app does.
    // The previous stop's times are dropped first: they must not be shown under this one.
    if (this.showsRealTimesFor(stop, serviceInfo)) {
      this.props.dispatch(resetStopAvailabilityTimesAction())
      this.props.dispatch(fetchStopAvailabilityTimesAction(stop.id, new Date(), this.servicesContainingStop(stop)))
    }
  }

  private buildMarkerInfoContent(stop: IStop, serviceInfo: IServiceInfo): HTMLElement {
    const { onMarkerClick, onShowSchedules } = this.props
    const { isMapCentered } = this.state

    const stopType = this.getStopType(stop)
    const stopMarkerContent = document.getElementById(stopType === 'origin' || stopType === 'destination' ? `${stopType}Dot` : `${stopType}Marker`)?.cloneNode(true) as HTMLElement

    const contentDiv = document.createElement('div')
    contentDiv.classList.add('marker-infowindow')

    const stopRow = document.createElement('div')
    stopRow.classList.add('stop-row')
    stopRow.innerHTML = `
      <span>${ stop.name }</span>
      <div>
        ${ stopMarkerContent.getHTML() }
      </div>
    `
    if (onMarkerClick && typeof onMarkerClick === 'function') {
      stopRow.onclick = () => {
        onMarkerClick({ ...stop, serviceInfo })
        if (!isMapCentered) this.handleRecenterClick()
        this.infoWindow.setOptions({})
        this.infoWindow.close()
        this.openedStopInfo = undefined
      }
    }
    contentDiv.appendChild(stopRow)

    if (!this.showsTimesFor(stop, serviceInfo)) return contentDiv

    // Like the app: the next departures are listed only by services that publish live times;
    // one with just a timetable offers the schedules button instead.
    const departures = this.showsRealTimesFor(stop, serviceInfo) ? this.nextDepartures : []
    if (departures.length) contentDiv.appendChild(this.buildDepartures(departures))

    if (onShowSchedules && typeof onShowSchedules === 'function') {
      const schedulesButton = document.createElement('button')
      schedulesButton.classList.add('schedules-button')
      schedulesButton.textContent = LiteralsService.get(departures.length ? 'seeMoreSchedules' : 'chooseHour', true)
      schedulesButton.onclick = (event) => {
        event.stopPropagation()
        this.infoWindow.close()
        this.openedStopInfo = undefined
        onShowSchedules({ ...stop, serviceInfo })
      }
      contentDiv.appendChild(schedulesButton)
    }

    return contentDiv
  }

  private buildDepartures(departures: IStopAvailabilityTimes[]): HTMLElement {
    const container = document.createElement('div')
    container.classList.add('departures')

    departures.forEach(departure => {
      // 'Ongoing' means the vehicle is under way, so the time is a live estimate.
      const isRealtime = departure.status === 'Ongoing'
      const row = document.createElement('div')
      row.classList.add('departure')
      const color = departure.serviceInfo?.color && departure.serviceInfo.color !== '#FFFFFFFF'
        ? departure.serviceInfo.color
        : theme.colors.greyLight
      row.style.borderLeftColor = color
      const time = isRealtime
        ? LiteralsService.get('inMinutes').replace('{0}', String(calculateMinutesToArrive(departure.expectedDateTime)))
        : moment(departure.expectedDateTime).format('HH:mm')
      row.innerHTML = `
        <img src="${ isRealtime ? images.realTime : images.clock }" alt="departure-icon" />
        <span class="departure-time">${ time }</span>
        <span class="departure-destination">${ LiteralsService.get('toDirection') } ${ departure.destination ?? '' }</span>
      `
      container.appendChild(row)
    })

    return container
  }

  // Mirrors the app: only expeditions already under way or fixed ones still to come.
  private get nextDepartures(): IStopAvailabilityTimes[] {
    const { stopAvailabilityTimes } = this.props
    if (!stopAvailabilityTimes) return []

    return stopAvailabilityTimes
      .filter(availability => {
        if (availability.expedition) return true
        if (availability.serviceAvailability?.expeditionType === IExpeditionType.Fixed) {
          return moment(availability.expectedDateTime).isAfter(moment())
        }
        return false
      })
      .slice(0, DEPARTURES_SHOWN_IN_INFO_WINDOW)
  }

  private servicesContainingStop(stop: IStop): IServiceInfo[] {
    const { serviceInfos } = this.props

    return serviceInfos.filter(serviceInfo => serviceInfo.stops?.some(serviceStop => serviceStop.id === stop.id))
  }

  private showsTimesFor(stop: IStop, serviceInfo: IServiceInfo): boolean {
    const services = [serviceInfo, ...this.servicesContainingStop(stop)]

    return services.some(service => service?.showRealTimes || service?.showTheoreticalTimes)
  }

  private showsRealTimesFor(stop: IStop, serviceInfo: IServiceInfo): boolean {
    const services = [serviceInfo, ...this.servicesContainingStop(stop)]

    return services.some(service => service?.showRealTimes)
  }

  private refreshMarkerInfo(): void {
    if (!this.openedStopInfo || !this.infoWindow?.isOpen) return

    const { stop, serviceInfo } = this.openedStopInfo
    this.infoWindow.setContent(this.buildMarkerInfoContent(stop, serviceInfo))
  }

  private updateMarkersIcon = (): void => {
    this.clusterer && this.clusterer.clearMarkers()
    this.markers = []
    this.fillClusterMarkers()
  }

  private updateMapLocation(lat: number, lng: number, onArrival?: () => void): void {
    const { zoomLevel } = this.props
    this.isProgrammaticCameraMove = true

    this.glideCameraTo({ lat, lng }, () => {
      this.map.setZoom(zoomLevel ?? 14)
      this.setMapBoundariesAndZoom()
      onArrival?.()
    })
  }

  // panTo only animates over short hops and setZoom/fitBounds snap on top of
  // it, so recentring is animated by hand to keep it from teleporting.
  private glideCameraTo(target: google.maps.LatLngLiteral, onArrival: () => void): void {
    const start = this.map?.getCenter()
    if (!start) return onArrival()

    if (this.cameraAnimation) cancelAnimationFrame(this.cameraAnimation)

    const from = { lat: start.lat(), lng: start.lng() }
    const startedAt = performance.now()
    const easeInOut = (time: number) =>
      time < 0.5 ? 2 * time * time : 1 - Math.pow(-2 * time + 2, 2) / 2

    const step = (now: number) => {
      const progress = Math.min((now - startedAt) / CAMERA_GLIDE_MS, 1)
      const eased = easeInOut(progress)

      this.map.setCenter({
        lat: from.lat + (target.lat - from.lat) * eased,
        lng: from.lng + (target.lng - from.lng) * eased
      })

      if (progress < 1) {
        this.cameraAnimation = requestAnimationFrame(step)
      } else {
        this.cameraAnimation = undefined
        onArrival()
      }
    }

    this.cameraAnimation = requestAnimationFrame(step)
  }

  private drawRoute(): void {
    const { path } = this.props

    if (path) {
      const coordinates: google.maps.LatLngLiteral[] = path.features
        .filter((c: any) => c !== null)
        .flatMap((feature: any) => {
          return feature.geometry.coordinates.map((coord: any) => {
            return { lat: coord[1], lng: coord[0] }
          })
        })
      const busPath = new google.maps.Polyline({
        path: coordinates,
        geodesic: false,
        strokeColor: '#000000',
        strokeOpacity: 1.0,
        strokeWeight: 2.5
      })
      busPath.setMap(this.map)
    }
  }

  private setMapBoundariesAndZoom() {
    // Framing the map is our doing, not the user panning away from the area.
    this.isProgrammaticCameraMove = true
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.isProgrammaticCameraMove = false
    })

    let markersAvailables: any[] = [];
    this.markers.forEach((marker) => {
      if (!marker.id.includes('disabled')) markersAvailables.push(marker)
    })
    const bounds = new google.maps.LatLngBounds()
    const coordinates: google.maps.LatLngLiteral[] = markersAvailables.map(m => m.position as google.maps.LatLngLiteral )
    coordinates.map(c => bounds.extend(c))
    this.map.fitBounds(bounds, {top: 55, right: 30, bottom: 55, left: 30})
  }

  private clearOutlines(): void {
    this.polygons.map((outline) => outline.setMap(null))
    this.polygons = []
  }

  private drawServiceOutlines(): void {
    const { isClusterMap, serviceInfos } = this.props
    const serviceInfoNotSelected = serviceInfos.every((serviceInfo) => !serviceInfo.selected)
    if (isClusterMap && serviceInfos) {
      serviceInfos.filter((serviceInfo: IServiceInfo) => serviceInfoNotSelected ? serviceInfo : !!serviceInfo.outlines && serviceInfo.selected)
      .forEach(serviceInfo => {
        serviceInfo.outlines.map((outline) => {
          if (outline && outline.geometry) {
            const outlineCoords: google.maps.LatLngLiteral[] = outline.geometry.coordinates
              .map((coord: any) => {
                return coord.map((c: any) => ({ lat: c[1], lng: c[0] }))
              })
            const polygon = new google.maps.Polygon({
              paths: outlineCoords,
              strokeColor: serviceInfo.color,
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: `${serviceInfo.color}`,
              fillOpacity: 0.35
            })
            polygon.setMap(this.map)
            this.polygons.push(polygon)
          }
        })
      })
    }
  }

  private isDisabled(stop: IStop): boolean {
    const { outBusStops, inStop, outStop, restrictedOrigins } = this.props
    if ((inStop || outStop) && !!outBusStops && outBusStops.length === 0) return true

    const isSelectable = !!outBusStops && outBusStops.some(dbs => dbs.id === stop.id)
    const isOrigin =
      inStop == null
        ? false
        : inStop && inStop.id === stop.id

    const checkIfIsRestricted = restrictedOrigins ? restrictedOrigins.includes(stop.id) : false;
    if (isOrigin) return false
    return inStop != null ? !!outBusStops && !!outBusStops.length && !isSelectable : checkIfIsRestricted
  }

  private handleRecenterClick() {
    const { dispatch } = this.props
    const { isMapCentered } = this.state
    if (isMapCentered) {
      dispatch(searchStopCoordinatesAction({ type: ISearchedType.location } as any))
    } else {
      this.updateMapLocation(
        this.region.latitude,
        this.region.longitude,
        () => this.setState({ isMapCentered: true })
      )
    }
  }
}

const mapStateToProps = ({ newReservation }: IStoreState) => ({
  stopAvailabilityTimes: newReservation.stopAvailabilityTimes
})

export default connectRedux(mapStateToProps, MapContainer)
